import { CircleAlertIcon, XIcon } from "lucide-react";
import type { ComposerFileAttachment, ComposerImageAttachment } from "../../composerDraftStore";
import { COARSE_POINTER_HIT_AREA_PAD_CLASS } from "../../lib/touchTarget";
import { getRestingComposerImagePreviewCounts } from "../composerFooterLayout";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "~/lib/utils";
import { buildExpandedImagePreview, type ExpandedImagePreview } from "./ExpandedImagePreview";

const EMPTY_FILES: readonly ComposerFileAttachment[] = [];

export function ComposerImageTray({
  images,
  files = EMPTY_FILES,
  nonPersistedImageIds,
  onExpandImage,
  onRemoveImage,
  onRemoveFile,
  resting,
}: {
  readonly images: readonly ComposerImageAttachment[];
  readonly files?: readonly ComposerFileAttachment[];
  readonly nonPersistedImageIds: ReadonlySet<string>;
  readonly onExpandImage?: ((preview: ExpandedImagePreview) => void) | undefined;
  readonly onRemoveImage?: ((id: string) => void) | undefined;
  readonly onRemoveFile?: ((id: string) => void) | undefined;
  readonly resting: boolean;
}) {
  const { visibleCount, overflowCount } = resting
    ? getRestingComposerImagePreviewCounts(images.length)
    : { visibleCount: images.length, overflowCount: 0 };
  const visibleImages = images.slice(0, visibleCount);
  return images.length > 0 || files.length > 0 ? (
    <div data-chat-composer-images="true" className="mb-3 flex flex-wrap gap-2">
      {visibleImages.map((image) => (
        <div
          key={image.id}
          className="relative h-16 w-16 overflow-hidden rounded-lg border border-border/80 bg-background"
        >
          {image.previewUrl ? (
            <button
              type="button"
              className="h-full w-full cursor-zoom-in"
              aria-label={`Preview ${image.name}`}
              onClick={() => {
                if (!onExpandImage) return;
                const preview = buildExpandedImagePreview(images, image.id);
                if (!preview) return;
                onExpandImage(preview);
              }}
            >
              <img src={image.previewUrl} alt={image.name} className="h-full w-full object-cover" />
            </button>
          ) : (
            <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-muted-foreground/70">
              {image.name}
            </div>
          )}
          {nonPersistedImageIds.has(image.id) ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <span
                    role="img"
                    aria-label="Draft attachment may not persist"
                    className="absolute left-1 top-1 inline-flex items-center justify-center rounded bg-background/85 p-0.5 text-warning-foreground"
                  >
                    <CircleAlertIcon className="size-3" />
                  </span>
                }
              />
              <TooltipPopup side="top" className="max-w-64 whitespace-normal leading-tight">
                Draft attachment could not be saved locally and may be lost on navigation.
              </TooltipPopup>
            </Tooltip>
          ) : null}
          <Button
            variant="ghost"
            size="icon-xs"
            className={cn(
              COARSE_POINTER_HIT_AREA_PAD_CLASS,
              "absolute right-1 top-1 bg-background/80 hover:bg-background/90",
            )}
            onClick={() => onRemoveImage?.(image.id)}
            aria-label={`Remove ${image.name}`}
          >
            <XIcon />
          </Button>
        </div>
      ))}
      {overflowCount > 0 ? (
        <button
          type="button"
          className="rounded-lg border border-border text-xs"
          aria-label={`View ${overflowCount} more attachments`}
          onClick={() => {
            const preview = buildExpandedImagePreview(images, images[visibleCount]?.id ?? "");
            if (preview) onExpandImage?.(preview);
          }}
        >
          +{overflowCount}
        </button>
      ) : null}
      {files.map((file) => (
        <div
          key={file.id}
          className="flex min-h-11 max-w-full items-center gap-2 rounded-lg border border-border/80 bg-background px-2 text-sm"
        >
          <Tooltip>
            <TooltipTrigger render={<span className="min-w-0 truncate" />}>
              {file.name}
            </TooltipTrigger>
            <TooltipPopup side="top">{file.name}</TooltipPopup>
          </Tooltip>
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatFileSize(file.sizeBytes)}
          </span>
          <Button
            variant="ghost"
            size="icon-xs"
            className={cn(COARSE_POINTER_HIT_AREA_PAD_CLASS, "shrink-0")}
            onClick={() => onRemoveFile?.(file.id)}
            aria-label={`Remove ${file.name}`}
          >
            <XIcon />
          </Button>
        </div>
      ))}
    </div>
  ) : null;
}

function formatFileSize(sizeBytes: number): string {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${Math.ceil(sizeBytes / 1024)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}
